/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { ShowDiffPlugin } from '../../showDiffPluginType';

import { ContributorTag } from './ContributorTag';

/**
 * One contributor tag, mounted by its diff decoration into the host element that decoration draws
 * (see `mountContributorTag`).
 *
 * The model is read from plugin state here rather than passed in, because ProseMirror skips `toDOM`
 * for a widget it reuses: stepping to the next change republishes the tags without redrawing their
 * hosts, so anything the tag needs to react to — `isActive` above all — has to arrive through a
 * subscription of its own. Renders nothing when this diff has no tag, which is the case for the
 * deleted half of a replacement: that half is folded into the inline tag.
 */
export const ContributorTagWidget = ({
	api,
	diffId,
	getEditorRoot,
}: {
	api?: ExtractInjectionAPI<ShowDiffPlugin>;
	diffId: string;
	/** This editor's own content root — see `ContributorTagProps.getEditorRoot`. */
	getEditorRoot: () => HTMLElement | null;
}): JSX.Element | null => {
	const { model } = useSharedPluginStateWithSelector(api, ['showDiff'], (state) => ({
		model: state.showDiffState?.contributorTags?.find((tag) => tag.diffId === diffId),
	}));

	if (!model) {
		return null;
	}

	return (
		<ContributorTag
			colorScheme={model.colorScheme}
			connectedContributor={model.connectedContributor}
			contributor={model.contributor}
			diffId={model.diffId}
			getEditorRoot={getEditorRoot}
			isActive={model.isActive}
			isInserted={model.isInserted}
			linkedDiffIds={model.linkedDiffIds}
		/>
	);
};
